'use client'

import { OTPInput, OTPInputContext } from 'input-otp'
import * as React from 'react'
import { cn } from 'src/lib/utils'

const InputOTPGroup = React.forwardRef<React.ElementRef<'div'>, React.ComponentPropsWithoutRef<'div'>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex w-full items-center justify-center gap-2 sm:gap-3', className)}
      data-slot="input-otp-group"
      {...props}
    />
  ),
)
InputOTPGroup.displayName = 'InputOTPGroup'

const InputOTPSlot = React.forwardRef<
  React.ElementRef<'div'>,
  React.ComponentPropsWithoutRef<'div'> & { index: number; hideCaret?: boolean }
>(({ index, className, 'aria-invalid': ariaInvalid, hideCaret = false, ...props }, ref) => {
  const inputContext = React.useContext(OTPInputContext)
  const { char, hasFakeCaret, isActive } = inputContext.slots[index] ?? {}

  return (
    <div
      ref={ref}
      aria-invalid={ariaInvalid}
      className={cn(
        'aspect-square w-full max-w-[78px] flex-1 rounded-[8px] border-0 font-evogria text-2xl sm:text-4xl relative flex items-center justify-center transition-all first:rounded-l-md first:border-l last:rounded-r-md',
        ariaInvalid
          ? 'bg-error-bg text-error-2 outline-none ring-0 focus-within:outline-none focus-within:ring-0'
          : 'bg-secondary-gray text-ivory focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2 focus-within:ring-offset-[#020000] border-y border-r border-input',
        isActive && !ariaInvalid && 'z-10 ring-2 ring-ring ring-offset-background',
        className,
      )}
      data-slot="input-otp-slot"
      data-state={isActive ? 'active' : 'inactive'}
      {...props}
    >
      {char}
      {!hideCaret && hasFakeCaret && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="h-6 w-px animate-caret-blink bg-foreground duration-1000" />
        </div>
      )}
    </div>
  )
})
InputOTPSlot.displayName = 'InputOTPSlot'

const InputOTPSeparator = React.forwardRef<React.ElementRef<'div'>, React.ComponentPropsWithoutRef<'div'>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} role="separator" className={cn('flex items-center', className)} {...props}>
      <div className="w-3 rounded-full bg-border" />
    </div>
  ),
)
InputOTPSeparator.displayName = 'InputOTPSeparator'

export interface InputOTPProps extends Omit<React.ComponentPropsWithoutRef<typeof OTPInput>, 'render' | 'children'> {
  children: React.ReactNode
}

const InputOTP = React.forwardRef<HTMLInputElement, InputOTPProps>(
  ({ className, containerClassName, children, ...props }, ref) => (
    <OTPInput
      ref={ref}
      containerClassName={cn('group flex w-full items-center justify-center has-[:disabled]:opacity-50', containerClassName)}
      className={cn('disabled:cursor-not-allowed', className)}
      {...props}
    >
      {children}
    </OTPInput>
  ),
)
InputOTP.displayName = 'InputOTP'

export { InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator }
